import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Wallet, Menu, X, Zap,
  Store, User, Home,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { RootState } from "../../redux/store";
import logo from "../../assets/app/logo.png";
import ClientProductOrders from "./ClientProductOrders";

/* ═══════════════════════════════════════════════
   TOKENS — PEEKABEBI: logo orange→red + cream/peach
   (matches CategoryDetails / ClientProductOrders)
═══════════════════════════════════════════════ */
const F = {
  pageBg:    "#FFF8F1",
  sidebar:   "#FFFFFF",
  topbar:    "rgba(255,255,255,0.97)",
  cardBg:    "#FFFFFF",
  surface:   "#FFF3E6",
  ink:       "#2B1A12",
  inkSoft:   "#3A2418",
  muted:     "#9C8A7E",
  mutedDim:  "#B79C87",
  orange:    "#FF7A1A",
  red:       "#FF3D57",
  green:     "#1FAA59",
  border:    "rgba(43,26,18,0.10)",
  borderOr:  "rgba(255,122,26,0.35)",
  grad:      "linear-gradient(135deg,#FF9A3D 0%,#FF3D57 100%)",
  gradH:     "linear-gradient(90deg,#FF9A3D 0%,#FF3D57 100%)",
  gradA:     "linear-gradient(90deg,#FFB347 0%,#FF3D57 50%,#FFB347 100%)",
};

const GRAD_H = F.gradH;
const GRAD_A = F.gradA;

/* ═══════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
  *,::before,::after{box-sizing:border-box;margin:0;padding:0;}
  button,input{font-family:inherit;cursor:pointer;}
  ::-webkit-scrollbar{width:3px;}
  ::-webkit-scrollbar-thumb{background:rgba(255,122,26,.22);border-radius:99px;}

  @keyframes gradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes pulseDot{0%,100%{opacity:1}50%{opacity:.35}}
  @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
  @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
  @keyframes orb{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(-18px,12px) scale(1.04)}66%{transform:translate(12px,-8px) scale(0.97)}}

  /* sidebar nav */
  .sd-nav{
    width:100%;display:flex;align-items:center;gap:10px;
    padding:11px 16px;border:none;background:transparent;
    cursor:pointer;transition:all .18s;font-family:'Montserrat',sans-serif;
    border-left:3px solid transparent;text-align:left;position:relative;overflow:hidden;
  }
  .sd-nav::before{
    content:'';position:absolute;inset:0;
    background:linear-gradient(90deg,rgba(255,122,26,.08),transparent);
    opacity:0;transition:opacity .18s;
  }
  .sd-nav:hover::before{opacity:1;}
  .sd-nav.on{
    background:linear-gradient(90deg,rgba(255,122,26,.1),rgba(255,61,87,.05));
    border-left-color:#FF3D57;
  }
  .sd-nav.on::before{opacity:1;}

  /* icon btn */
  .sd-icon{
    width:34px;height:34px;border:none;background:transparent;
    display:flex;align-items:center;justify-content:center;
    cursor:pointer;transition:all .15s;border-radius:9px;
    border:1px solid transparent;
  }
  .sd-icon:hover{background:rgba(255,122,26,.1);border-color:rgba(255,61,87,.3);}

  /* mobile footer tab */
  .ftab{
    display:flex;flex-direction:column;align-items:center;gap:3px;
    font-family:'Montserrat',sans-serif;font-size:10px;font-weight:600;
    cursor:pointer;padding:4px 10px;color:#C9A98E;
    border:none;background:none;transition:color .15s;flex:1;
  }
  .ftab.on{color:#FF7A1A;}

  /* watermark — huge, faint, fixed, behind all layout */
  .page-watermark{
    position:fixed;top:50%;left:50%;
    width:min(60vw,560px);height:min(60vw,560px);
    transform:translate(-50%,-50%);
    opacity:.035;pointer-events:none;z-index:0;
    object-fit:contain;
  }
  /* sidebar watermark — small, sits low behind the nav */
  .sidebar-watermark{
    position:absolute;left:50%;bottom:70px;
    width:160px;height:160px;transform:translateX(-50%) rotate(-6deg);
    opacity:.04;pointer-events:none;z-index:0;object-fit:contain;
  }
`;

type TabKey = "orders" | "stores" | "profile";

const NAV: { id: TabKey; label: string; icon: React.ElementType }[] = [
  { id: "orders",  label: "My Orders", icon: ShoppingBag },
  { id: "stores",  label: "Browse",    icon: Store       },
  { id: "profile", label: "Profile",   icon: User        },
];

const FOOTER_TABS = NAV;

/* ═══════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════ */
const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const params   = useParams<{ userId?: string }>();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const userId   = params.userId || authUser?.userId;

  const [activeTab, setActiveTab] = useState<TabKey>("orders");
  const [mobile,    setMobile]    = useState(window.innerWidth < 1024);
  const [sidebar,   setSidebar]   = useState(false);

  useEffect(() => {
    if (!authUser) { navigate("/login"); return; }
    if (!userId)   { navigate("/"); }
  }, [authUser, userId, navigate]);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const navClick = (id: TabKey) => {
    if (id === "stores")  { navigate(`/ProductsHome/${userId}`); return; }
    if (id === "profile") { navigate(`/profile/${userId}`); return; }
    setActiveTab(id);
    if (mobile) setSidebar(false);
  };

  const renderMain = () => {
    switch (activeTab) {
      case "orders": return <ClientProductOrders />;
      default:       return <ClientProductOrders />;
    }
  };

  const initial = (authUser?.firstName || authUser?.email || "A")[0].toUpperCase();
  const name    = authUser?.firstName || authUser?.displayName || "Customer";

  /* ════════════════════════ RENDER ════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: F.pageBg, fontFamily: "'Montserrat',sans-serif", color: F.ink, display: "flex", position: "relative", overflow: "hidden" }}>
      <style>{CSS}</style>

      {/* page-wide watermark logo — behind everything */}
      <img src={logo} alt="" className="page-watermark" />

      {/* 3D background orbs */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,122,26,.12),transparent 70%)", animation: "orb 12s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,61,87,.08),transparent 70%)", animation: "orb 15s ease-in-out infinite reverse" }} />
        <div style={{ position: "absolute", top: "40%", left: "40%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,154,61,.06),transparent 70%)", animation: "orb 10s ease-in-out infinite" }} />
      </div>

      {/* mobile sidebar backdrop */}
      <AnimatePresence>
        {mobile && sidebar && (
          <motion.div key="ov"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSidebar(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(43,26,18,.45)", zIndex: 998, backdropFilter: "blur(5px)" }} />
        )}
      </AnimatePresence>

      {/* ════════════════ SIDEBAR ════════════════ */}
      <aside style={{
        width: 240,
        background: F.sidebar,
        borderRight: `1px solid ${F.border}`,
        display: "flex", flexDirection: "column",
        position: mobile ? "fixed" : "sticky",
        top: 0, height: "100vh", zIndex: 999,
        transform: mobile && !sidebar ? "translateX(-100%)" : "translateX(0)",
        transition: "transform .32s cubic-bezier(.4,0,.2,1)",
        boxShadow: mobile && sidebar
          ? "8px 0 48px rgba(255,122,26,.15),2px 0 16px rgba(255,61,87,.08)"
          : `1px 0 0 ${F.border}`,
        flexShrink: 0,
        overflow: "hidden",
      }}>
        {/* sidebar watermark — behind nav content */}
        <img src={logo} alt="" className="sidebar-watermark" />

        {/* animated gradient top stripe */}
        <div style={{ height: 3, background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 4s linear infinite", position: "relative", zIndex: 1 }} />

        {/* logo block */}
        <div style={{ padding: "18px 18px 16px", borderBottom: `1px solid ${F.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 1, background: F.sidebar }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* 3D gradient ring logo */}
            <div style={{ position: "relative", display: "inline-flex" }}>
              <div style={{ position: "absolute", inset: -2.5, borderRadius: "50%", background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 3s linear infinite", zIndex: 0 }} />
              <div style={{ position: "relative", zIndex: 1, background: F.sidebar, borderRadius: "50%", padding: 2.5, display: "inline-flex" }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: F.surface }}>
                  <img src={logo} alt="PEEKABEBI" style={{ width: "80%", height: "80%", objectFit: "contain" }} />
                </div>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 1.6, background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 4s linear infinite", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontFamily: "'Baloo 2',sans-serif" }}>
                PEEKABEBI
              </div>
              <div style={{ fontSize: 8, color: F.mutedDim, letterSpacing: ".18em", marginTop: 1 }}>CUSTOMER PORTAL</div>
            </div>
          </div>
          {mobile && (
            <button className="sd-icon" onClick={() => setSidebar(false)}>
              <X size={15} color={F.muted} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* user greeting */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${F.border}`, position: "relative", zIndex: 1, background: F.sidebar }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
              style={{ width: 40, height: 40, borderRadius: "50%", background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s linear infinite", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 6px 18px rgba(255,122,26,.35),0 2px 6px rgba(255,61,87,.2),inset 0 1px 3px rgba(255,255,255,.25)" }}>
              <span style={{ fontSize: 16, fontWeight: 900, color: "#fff", fontFamily: "'Baloo 2',sans-serif" }}>{initial}</span>
            </motion.div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: F.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Baloo 2',sans-serif" }}>
                Hey, {name} 👋
              </div>
              <div style={{ fontSize: 9, color: F.mutedDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
                {authUser?.email || ""}
              </div>
            </div>
          </div>
        </div>

        {/* nav links */}
        <nav style={{ flex: 1, overflowY: "auto", padding: "10px 0", position: "relative", zIndex: 1 }}>
          {NAV.map(({ id, label, icon: Icon }) => {
            const on = activeTab === id;
            return (
              <button key={id} className={`sd-nav${on ? " on" : ""}`} onClick={() => navClick(id)}>
                {/* 3D icon badge */}
                <div style={{ width: 30, height: 30, borderRadius: 9, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .18s", background: on ? "linear-gradient(135deg,rgba(255,122,26,.2),rgba(255,61,87,.12))" : "rgba(43,26,18,.04)", border: `1px solid ${on ? "rgba(255,61,87,.35)" : "rgba(43,26,18,.07)"}`, boxShadow: on ? "0 3px 10px rgba(255,122,26,.18),inset 0 1px 2px rgba(255,255,255,.4)" : "none" }}>
                  <Icon size={14} color={on ? F.orange : F.muted} strokeWidth={on ? 2.2 : 1.6} />
                </div>
                <span style={{ fontSize: 12, fontWeight: on ? 700 : 500, color: on ? F.ink : F.muted, flex: 1, transition: "all .15s" }}>{label}</span>
                {on && (
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite, pulseDot 2s infinite", flexShrink: 0, boxShadow: "0 2px 6px rgba(255,61,87,.4)" }} />
                )}
              </button>
            );
          })}
        </nav>

        {/* quick shop CTA */}
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${F.border}`, position: "relative", zIndex: 1, background: F.sidebar }}>
          <button onClick={() => navigate(`/ProductsHome/${userId}`)}
            style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: "none", background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", color: "#fff", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer", boxShadow: "0 6px 20px rgba(255,122,26,.28),0 2px 8px rgba(255,61,87,.15)", fontFamily: "'Baloo 2',sans-serif" }}>
            <Zap size={13} /> Shop Now
          </button>
        </div>
      </aside>

      {/* ════════════════ MAIN ════════════════ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, position: "relative", zIndex: 1 }}>

        {/* topbar */}
        <header style={{ height: 56, background: F.topbar, backdropFilter: "blur(18px)", borderBottom: `1px solid ${F.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 22px", position: "sticky", top: 0, zIndex: 100, flexShrink: 0, boxShadow: "0 2px 16px rgba(255,122,26,.07)" }}>
          {/* animated gradient stripe */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2.5, background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 4s linear infinite" }} />

          {/* left */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {mobile && (
              <button className="sd-icon" onClick={() => setSidebar(true)}>
                <Menu size={17} color={F.muted} strokeWidth={1.5} />
              </button>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              {/* 3D gradient accent bar */}
              <div style={{ width: 4, height: 20, borderRadius: 99, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", boxShadow: "0 2px 8px rgba(255,61,87,.35)" }} />
              <h1 style={{ fontSize: 15, fontWeight: 800, color: F.ink, letterSpacing: -.2, fontFamily: "'Baloo 2',sans-serif" }}>
                {NAV.find(n => n.id === activeTab)?.label ?? "Dashboard"}
              </h1>
            </div>
          </div>

          {/* right */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* live badge */}
            <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 11px", borderRadius: 999, background: "rgba(31,170,89,.08)", border: "1px solid rgba(31,170,89,.22)" }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: F.green, animation: "pulseDot 2s infinite" }} />
              <span style={{ fontSize: 9, fontWeight: 800, color: F.green, letterSpacing: ".1em", fontFamily: "'Baloo 2',sans-serif" }}>LIVE</span>
            </div>
            {/* 3D avatar */}
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: .94 }}
              onClick={() => navigate(`/profile/${userId}`)}
              style={{ width: 32, height: 32, borderRadius: "50%", background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s linear infinite", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 4px 14px rgba(255,122,26,.32),inset 0 1px 3px rgba(255,255,255,.25)" }}>
              <span style={{ fontSize: 12, fontWeight: 900, color: "#fff", fontFamily: "'Baloo 2',sans-serif" }}>{initial}</span>
            </motion.div>
          </div>
        </header>

        {/* page content */}
        <main style={{ flex: 1, overflowY: "auto", background: "transparent", paddingBottom: mobile ? 72 : 0 }}>
          <AnimatePresence mode="wait">
            <motion.div key={activeTab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: .22, ease: [.2, 0, .1, 1] }}
              style={{ minHeight: "100%" }}>
              {renderMain()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* ════════════ MOBILE FOOTER TABS ════════════ */}
      {mobile && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: 64, background: "rgba(255,255,255,.97)", borderTop: `1px solid ${F.border}`, display: "flex", justifyContent: "space-around", alignItems: "center", zIndex: 200, backdropFilter: "blur(14px)", boxShadow: "0 -4px 24px rgba(255,122,26,.1)" }}>
          {/* gradient top line */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: GRAD_A, backgroundSize: "300% 100%", animation: "gradSpin 4s linear infinite" }} />

          <button className="ftab" onClick={() => navigate(`/ProductsHome/${userId}`)}>
            <Home size={21} color={F.mutedDim} />Home
          </button>

          {FOOTER_TABS.map(({ id, label, icon: Icon }) => {
            const on = activeTab === id && id === "orders";
            return (
              <button key={id} className={`ftab${on ? " on" : ""}`} onClick={() => navClick(id)}>
                <div style={{ position: "relative" }}>
                  <Icon size={21} color={on ? F.orange : F.mutedDim} />
                  {on && <div style={{ position: "absolute", bottom: -4, left: "50%", transform: "translateX(-50%)", width: 4, height: 4, borderRadius: "50%", background: F.red }} />}
                </div>
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;